import axios from './axios';

const MOBILE_APP_INSTANCE_KEY = 'social.mobile.appInstanceId';

const getCapacitor = () =>
  typeof window !== 'undefined' ? window.Capacitor : undefined;

const getPlugin = (name) => getCapacitor()?.Plugins?.[name];
const getInjectedRuntimeContext = () => {
  if (typeof window === 'undefined') return null;
  return Reflect.get(window, '__LIGA_RUNTIME_CONTEXT__');
};

const getLigaAndroidBridge = () =>
  typeof window !== 'undefined' ? window.LigaAndroid : undefined;

const normalizeRuntimePlatform = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return ['web', 'android', 'ios'].includes(normalized) ? normalized : 'web';
};

export const getRuntimePlatform = () => {
  const injectedPlatform = normalizeRuntimePlatform(
    getInjectedRuntimeContext()?.platform
  );
  if (injectedPlatform !== 'web') {
    return injectedPlatform;
  }

  const capacitor = getCapacitor();
  const capacitorPlatform = normalizeRuntimePlatform(
    capacitor?.getPlatform?.()
  );

  if (capacitorPlatform !== 'web') {
    return capacitorPlatform;
  }

  const ligaAndroidBridge = getLigaAndroidBridge();
  if (typeof ligaAndroidBridge?.getRuntimePlatform === 'function') {
    try {
      return normalizeRuntimePlatform(ligaAndroidBridge.getRuntimePlatform());
    } catch (_error) {
      return 'web';
    }
  }

  if (typeof ligaAndroidBridge?.isNativeApp === 'function') {
    try {
      return ligaAndroidBridge.isNativeApp() ? 'android' : 'web';
    } catch (_error) {
      return 'web';
    }
  }

  return capacitorPlatform;
};

export const isNativeMobileApp = () => getRuntimePlatform() !== 'web';

export const getNativePlatform = () => getRuntimePlatform();

export const getRuntimeContext = () => {
  const platform = getRuntimePlatform();
  return {
    platform,
    isNativeApp: platform !== 'web',
    isAndroid: platform === 'android',
    isIOS: platform === 'ios',
  };
};

const getAppInstanceId = () => {
  if (typeof window === 'undefined') return null;

  const existing = window.localStorage.getItem(MOBILE_APP_INSTANCE_KEY);
  if (existing) return existing;

  const generated =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(MOBILE_APP_INSTANCE_KEY, generated);
  return generated;
};

export const registerNativePushToken = async ({ token, platform }) => {
  const normalizedToken = String(token || '').trim();
  if (!normalizedToken) return null;

  const response = await axios.post('/api/users/my/devices/push-token', {
    platform: platform || getNativePlatform(),
    token: normalizedToken,
    appInstanceId: getAppInstanceId(),
    locale: navigator.language || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  });

  return response.data;
};

export const unregisterNativePushToken = async () => {
  const appInstanceId = getAppInstanceId();
  if (!appInstanceId) return null;

  const response = await axios.delete('/api/users/my/devices/push-token', {
    data: {
      appInstanceId,
    },
  });

  return response.data;
};

let hasAttachedPushListeners = false;

export const attachNativePushListeners = () => {
  if (!isNativeMobileApp() || hasAttachedPushListeners) return false;

  const App = getPlugin('App');
  const PushNotifications = getPlugin('PushNotifications');
  if (!PushNotifications?.addListener && !App?.addListener) return false;

  hasAttachedPushListeners = true;

  App?.addListener?.('appUrlOpen', ({ url }) => {
    if (typeof url !== 'string' || !url) return;
    const nextUrl = new URL(url);
    window.location.assign(
      `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}` || '/'
    );
  });

  PushNotifications?.addListener?.('registration', (token) => {
    registerNativePushToken({
      token: token?.value,
      platform: getNativePlatform(),
    }).catch(() => {});
  });

  PushNotifications?.addListener?.('registrationError', () => {});

  PushNotifications?.addListener?.(
    'pushNotificationActionPerformed',
    (event) => {
      const targetUrl = event?.notification?.data?.url;
      if (typeof targetUrl === 'string' && targetUrl) {
        window.location.assign(targetUrl);
      }
    }
  );

  return true;
};

export const requestNativePushRegistration = async () => {
  if (!isNativeMobileApp()) {
    return {
      permission: 'unsupported',
      subscription: null,
    };
  }

  attachNativePushListeners();

  const PushNotifications = getPlugin('PushNotifications');
  if (!PushNotifications?.requestPermissions || !PushNotifications?.register) {
    return {
      permission: 'unsupported',
      subscription: null,
    };
  }

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') {
    return {
      permission: 'denied',
      subscription: null,
    };
  }

  await PushNotifications.register();
  return {
    permission: 'granted',
    subscription: null,
  };
};

export const shareWithNativeApp = async ({ title, text, url }) => {
  if (!isNativeMobileApp()) return false;

  const Share = getPlugin('Share');
  if (!Share?.share) return false;

  await Share.share({
    title,
    text,
    url,
  });

  return true;
};

export const sharePostExternally = async (postSharePayload) => {
  const runtimeContext = getRuntimeContext();
  if (!runtimeContext.isAndroid) return false;

  const ligaAndroidBridge = getLigaAndroidBridge();
  if (typeof ligaAndroidBridge?.sharePostExternally === 'function') {
    try {
      return Boolean(
        ligaAndroidBridge.sharePostExternally(
          JSON.stringify(postSharePayload || {})
        )
      );
    } catch (_error) {
      return false;
    }
  }

  const shareText = [postSharePayload?.text, postSharePayload?.postUrl]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join('\n\n');

  return shareWithNativeApp({
    title: postSharePayload?.authorName || postSharePayload?.title || 'Liga',
    text: shareText,
    url: postSharePayload?.postUrl || '',
  });
};

export const openNativePostComposer = async () => {
  const runtimeContext = getRuntimeContext();
  if (!runtimeContext.isAndroid) return false;

  const ligaAndroidBridge = getLigaAndroidBridge();
  if (typeof ligaAndroidBridge?.openNativePostComposer === 'function') {
    try {
      return Boolean(ligaAndroidBridge.openNativePostComposer());
    } catch (_error) {
      return false;
    }
  }

  return false;
};

export const requestNativeGoogleIdToken = async ({ webClientId }) => {
  if (!isNativeMobileApp()) {
    throw new Error('Login nativo indisponivel fora do app instalado.');
  }

  if (!webClientId) {
    throw new Error('Google Client ID nao configurado para o app.');
  }

  const SocialLogin = getPlugin('SocialLogin');
  if (SocialLogin?.initialize && SocialLogin?.login) {
    await SocialLogin.initialize({
      google: {
        webClientId,
      },
    });

    const result = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['email', 'profile'],
      },
    });

    const token =
      result?.result?.idToken ||
      result?.result?.authentication?.idToken ||
      result?.idToken ||
      null;

    if (!token) {
      throw new Error('Google nativo nao retornou token de identidade.');
    }

    return token;
  }

  const GoogleAuth = getPlugin('GoogleAuth');
  if (GoogleAuth?.initialize && GoogleAuth?.signIn) {
    await GoogleAuth.initialize({
      clientId: webClientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: false,
    }).catch(() => {});
  }

  if (GoogleAuth?.signIn) {
    const result = await GoogleAuth.signIn();
    const token = result?.authentication?.idToken || result?.idToken || null;
    if (!token) {
      throw new Error('Google nativo nao retornou token de identidade.');
    }
    return token;
  }

  throw new Error('Plugin de login Google nativo nao encontrado no app.');
};
